/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import {type PhotoForm} from 'src/photoForm/validation';
import {UPLOAD_API_BASE_URL} from 'src/utils/environment';
import ky, {type KyInstance, type RetryOptions} from 'ky';
import {HttpHeaders} from 'src/utils/HttpHeaders';
import {
  type RecommendationResult,
  RecommendationResultSchema,
} from 'src/api/APIResponses';

type UploadClientOptions = {
  /** Overrides the default retry options */
  retry?: RetryOptions;
};

export class UploadClient {
  private options: UploadClientOptions;
  private api: KyInstance;

  /**
   * Creates an UploadClient.
   * @param options Options to adjust request behavior
   */
  constructor(options: UploadClientOptions = {}) {
    this.options = options;
    this.api = ky.create({
      prefixUrl: UPLOAD_API_BASE_URL,
      retry: this.options.retry,
      hooks: {
        beforeError: [
          async (error, state) => {
            console.error(`[UploadClient] request failed:`, error.name, error.message);
            return error;
          }
        ],
      },
    });
  }

  async uploadPhotoForm(form: PhotoForm): Promise<RecommendationResult> {
    console.debug('UploadClient.uploadPhotoForm invoked with form ', form);

    // Validate token before building FormData. Throwing inside ky's
    // beforeRequest hook after the Request body is constructed leaves
    // Node 24's ReadableStream in a closed state, poisoning later requests.
    const token = this.getToken();

    const formData = new FormData();
    for (const image of form.images) {
      if (image.compressionStatus !== 'success') {
        console.warn(
          `Image ${image.inputFile.name} was not compressed successfully. Uploading uncompressed image.`,
        );
      }

      const fileToUpload =
        image.compressionStatus === 'success'
          ? image.compressedFile
          : image.inputFile;
      formData.append('images', fileToUpload);
    }

    // Append locale if present
    if (form.locale) {
      formData.append('locale', form.locale);
    }

    const response = await this.api.post(
      '', // No path for the upload API, use prefixUrl directly
      {
        body: formData,
        credentials: 'include',
        timeout: 60000,
        retry: this.options.retry,
        headers: {
          [HttpHeaders.Authorization]: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to upload photo form', {
        cause: response,
      });
    }

    return RecommendationResultSchema.parse(await response.json());
  }

  private getToken(): string {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    if (!token) {
      throw new Error('Could not get token from search params');
    }

    return token;
  }
}
